/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| 
 | |_| | | | | |_) || |  / / | | |  \| | | | | || | 
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___|
                                                                                                                                                                                                                                                                                                                                       
=========================================================
* Horizon UI - v1.1.0
=========================================================

* Product Page: https://www.horizon-ui.com/
* Copyright 2023 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// Chakra imports
import {
  FormControl,
  FormErrorMessage,
  Spacer,
  InputGroup,
  Box,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Stack,
  Button,
  Select,
  Card,
  CardHeader,
  CardBody,
  Text,
  Heading,
  SimpleGrid,
  Input,
  InputRightElement,
  useDisclosure,
  useToast,
  CardFooter,
} from "@chakra-ui/react";

import React, { useEffect, useState} from "react";
import EmojiPicker from "emoji-picker-react";
import { AddIcon } from "@chakra-ui/icons";
import ChipTable from "views/admin/addUsers/components/ChipTable";

import {
  columnsDataCheck,
} from "views/admin/addUsers/variables/columnsData";

export default function UserReports() {
  const authToken = localStorage.getItem('authToken');
  const userData = JSON.parse(localStorage.getItem('userData'));
  const [alert, setAlert] = useState(['', '', ''])
  
  const { isOpen: isOrgOpen, onOpen: onOrgOpen, onClose: onOrgClose } = useDisclosure();
  const [organizationName, setOrganizationName] = useState('');
  const [orgNameInvalid, setOrgNameInvalid] = useState(false);
  const [organizationID, setOrganizationID] = useState(userData['organization']['$oid']);
  const [organization, setOrganization] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [newOrg, setNewOrg] = useState(false);

  const { isOpen: isDepOpen, onOpen: onDepOpen, onClose: onDepClose } = useDisclosure();
  const [departmentName, setDepartmentName] = useState('');
  const [depNameInvalid, setDepNameInvalid] = useState(false);
  const [departmentIcon, setDepartmentIcon] = useState('');
  const [depIconInvalid, setDepIconInvalid] = useState('');
  const [departmentID, setDepartmentID] = useState('');
  const [department, setDepartment] = useState('');
  const [departments, setDepartments] = useState([]);
  const [newDep, setNewDep] = useState(false);
  
  const { isOpen: isUserOpen, onOpen: onUserOpen, onClose: onUserClose } = useDisclosure();
  const [departmentInvalid, setDepartmentInvalid] = useState(false);
  const [userFirstName, setUserFirstName] = useState('');
  const [userFirstNameInvalid, setUserFirstNameInvalid] = useState(false);
  const [userLastName, setUserLastName] = useState('');
  const [userLastNameInvalid, setUserLastNameInvalid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userEmailInvalid, setUserEmailInvalid] = useState(false);
  const [userPassword, setUserPassword] = useState('');
  const [userPasswordInvalid, setUserPasswordInvalid] = useState(false);
  const [userHidePassword, setUserHidePassword] = useState(false);

  const [chips, setChips] = useState([]);

  const delay = ms => new Promise(
    resolve => setTimeout(resolve, ms)
  )

  const toast = useToast();

  useEffect(() => {
    if (alert[0] !== '') {
      toast({
        title: alert[1],
        description: alert[2],
        status: alert[0],
        duration: 5000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  }, [alert]);

  useEffect(() => {
    async function clearInputInvalid() {
      await delay(5000)
      setOrgNameInvalid(false)
      setDepNameInvalid(false)
      setDepIconInvalid(false)
      setDepartmentInvalid(false)
      setUserFirstNameInvalid(false)
      setUserLastNameInvalid(false)
      setUserEmailInvalid(false)
      setUserPasswordInvalid(false)
    }
    clearInputInvalid()
  }, [orgNameInvalid, depNameInvalid, depIconInvalid, departmentInvalid, userFirstNameInvalid, userLastNameInvalid, userEmailInvalid, userPasswordInvalid])

  // fetch organizations
  useEffect(() => {
    const options = {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`
      } 
    }
    
    if (!userData['is_super_user']) return

    fetch(`https://api.beehiveproject.no/organization`, options)
    .then(response => response.json())
    .then(data => {
      setOrganizations(data);
      setNewOrg(false);
    })
    .catch(error => console.log(error));
  }, [authToken, newOrg])
  
  // Fetch departments
  const fetchDepartments = async () => {
    try {
      const response = await fetch(
        `https://api.beehiveproject.no/organization/${organizationID}/departments`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const data = await response.json();
      setDepartments(data);
      setNewDep(false);

      return data;
    } catch (error) {
      console.error("Failed to fetch departments:", error);
      return [];
    }
  };
  

  // Fetch chips
  const fetchChips = async () => {
    if (!organizationID) return
    const latestDepartments = await fetchDepartments();

    try {
      const chipResponse = await fetch(`https://api.beehiveproject.no/organization/${organizationID}/chips`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const chips = await chipResponse.json();

      const data = chips
        .map((chip) => {
          const latestDepartment = latestDepartments.find(
            (dep) => dep._id.$oid === chip.department?.$oid
          );

          return {
            ...chip,
            department: {
              $oid: latestDepartment?._id.$oid,
              name: latestDepartment?.name || "Ukjent avdeling",
            },
          };
        });

      setChips(data);
    } catch (error) {
      console.error("Failed to fetch chips:", error);
    }
  };

  useEffect(() => {
    if (organizationID) {
      fetchChips();
    }
  }, [organizationID, newOrg, newDep]);


  const createOrganization = () => {
    if (organizationName === '')
      {
        setOrgNameInvalid(true)
        return
      }
    onOrgClose()
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: `{"name": "${organizationName}"}`
    };

    fetch('https://api.beehiveproject.no/organization', options)
      .then(response => {
        if (response.status === 200)
          {
          setAlert(['success', 'Suksess', `Organisasjonen ${organizationName} ble opprettet`])
          setNewOrg(true);
          setOrganizationName('');
          
          }
        else 
        {
          setAlert(['error', 'Feil', 'En feil oppsto under oppretting av organisasjon'])
        }
      })
      .then(data => {
        
      })
      .catch(error => console.error(error));
  }

  const createDepartment = () => {
    if (departmentName === '')
      {
        setDepNameInvalid(true)
        return
      }
    else if (departmentIcon === '')
      {
        setDepIconInvalid(true)
        return
      }
    
    onDepClose()
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: `{"name": "${departmentName}",
              "organization": "${organizationID}",
              "icon": "${departmentIcon}"
              }`
    };

    fetch('https://api.beehiveproject.no/department', options)
      .then(response => {
        if (response.status === 200)
          {
          setAlert(['success', 'Suksess', `Avdelingen ${departmentIcon} ${departmentName} ble opprettet`])
          setNewDep(true);
          setDepartmentName('');
          setDepartmentIcon('');
          }
          else 
          {
            setAlert(['error', 'Feil', 'En feil oppsto under oppretting av avdeling'])
          }
      })
      .then(data => {
        
      })
      .catch(error => console.error(error));
  }

  const createUser = () => {
    let inputs = [department,userEmail, userFirstName, userLastName, userPassword]
    let inputInvalids = [setDepartmentInvalid, setUserEmailInvalid, setUserFirstNameInvalid, setUserLastNameInvalid, setUserPasswordInvalid]
    let abort = false
    for (var i = 0; i < inputs.length; i++)
      {
        if (inputs[i] === '')
          {
            inputInvalids[i](true)
            abort = true
          }
      }

    if (abort)
      return

    onUserClose()
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: `{"first_name": "${userFirstName}",
              "last_name": "${userLastName}",
              "email": "${userEmail}",
              "password": "${userPassword}",
              "organization": "${organizationID}",
              "department": "${departmentID}"
              }`
    };

    fetch('https://api.beehiveproject.no/auth/signup', options)
      .then(response => {
        if (response.status === 200) {
          setAlert(['success', 'Suksess', `Brukeren for ${userFirstName} ${userLastName} har blitt opprettet`])
          setUserFirstName('');
          setUserLastName('');
          setUserEmail('');
          setUserPassword('');
        } else {
          setAlert(['error', 'Feil', 'En feil oppsto under oppretting av ny bruker'])
        }
      })
      .then(data => {})
      .catch(error => console.error(error));
  }

  
  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {userData['is_super_user'] && 
      <Stack direction={'row'} spacing='4'>
        <Select width={300} marginBottom={5} variant="filled" placeholder={organization['name']}
          onChange={async (event) => {
            let data = JSON.parse(event.target.value)
            const newOrgID = data['_id']['$oid']

            setOrganizationID(newOrgID)
            setOrganization(data['name'])
          }}>
          {organizations.map((org) => <option value={JSON.stringify(org)} key={org['_id']['$oid']}>{org['name']}</option>)}
        </Select>
        <Button colorScheme='brand' onClick={onOrgOpen} leftIcon={<AddIcon/>}>Ny Organisasjon</Button>
        <Modal isOpen={isOrgOpen} onClose={onOrgClose}>
          <ModalOverlay/>
          <ModalContent>
            <ModalHeader>Opprett Ny Organisasjon</ModalHeader>
            <ModalCloseButton/>
            <ModalBody>
            <Stack spacing='4'>
              <Heading size='sm'>Organisasjonsnavn</Heading>
              <FormControl isInvalid={orgNameInvalid} isRequired>
              <Input value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} placeholder="Gi organisasjonen et navn"/>
              <FormErrorMessage>Navn er påkrevd</FormErrorMessage>
              </FormControl>
            </Stack>
            </ModalBody>
            <ModalFooter>
            <Button onClick={onOrgClose} colorScheme='red'>Avbryt</Button>
            <Spacer/>
            <Button onClick={createOrganization} colorScheme='brand'>Opprett</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Stack>}
      <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='20px' mb='20px'>
        <Card>
          <CardHeader>
            <Heading size='md'>Avdelinger</Heading>
          </CardHeader>
          <CardBody>
            <Box>
              {departments.map((dep, index) => <Text key={index}>{dep['icon']} {dep['name']}</Text>)}
            </Box>
          </CardBody>
          {userData['is_org_admin'] &&
          <CardFooter>
            <Button onClick={onDepOpen} leftIcon={<AddIcon/>} colorScheme='brand'>Ny Avdeling</Button>
            <Modal isOpen={isDepOpen} onClose={onDepClose}>
              <ModalOverlay/>
              <ModalContent>
                <ModalHeader>Opprett Ny Avdeling</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                  <Stack spacing='4'>
                    <Heading size='sm'>Avdelingsnavn</Heading>
                    <FormControl isRequired isInvalid={depNameInvalid}>
                      <Input value={departmentName} onChange={(event) => setDepartmentName(event.target.value)} placeholder='Gi avdelingen et navn' />
                      <FormErrorMessage>Navn er påkrevd</FormErrorMessage>
                    </FormControl>
                    <Stack spacing='4'>
                      <Heading size='sm'>Ikon</Heading>
                      <Stack spacing='2' direction={'row'}>
                        <Box height={100} width={100}>
                          <Center>
                          <Text fontSize='5xl' >{departmentIcon}</Text>
                          </Center>
                        </Box>
                        <EmojiPicker open={true} height={400} width={700} onEmojiClick={(emoji) => setDepartmentIcon(emoji["emoji"])} skinTonesDisabled={true} searchDisabled={true} emojiStyle="native" previewConfig={{showPreview: false}}
                                  categories={['animals_nature', 'food_drink', 'travel_places', 'activities', 'objects', 'symbols', 'flags']}></EmojiPicker>
                      </Stack>
                    </Stack>
                  </Stack>
                </ModalBody>
                <ModalFooter>
                  <Button onClick={onDepClose} colorScheme='red'>Avbryt</Button>
                  <Spacer/>
                  <Button onClick={createDepartment} colorScheme='brand'>Opprett</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </CardFooter>}
        </Card>
        <Card>
          <CardHeader>
            <Heading size='md'>Brukere</Heading>
          </CardHeader>
          <CardBody>
            {/*TODO: Implement user list here*/}
          </CardBody>
          {userData['is_org_admin'] &&
          <CardFooter>
            <Button onClick={onUserOpen} leftIcon={<AddIcon/>} colorScheme='brand'>Ny Bruker</Button>
            <Modal isOpen={isUserOpen} onClose={onUserClose}>
              <ModalOverlay/>
              <ModalContent>
                <ModalHeader>Opprett Ny Bruker</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                  <Stack spacing='4'>
                    <Heading size='sm'>Avdeling</Heading>
                    <FormControl isRequired isInvalid={departmentInvalid}>
                      <Select variant="outline" placeholder='Velg en avdeling'
                        onChange={(event) => {
                          let data = JSON.parse(event.target.value)
                          setDepartmentID(data['_id']['$oid'])
                          setDepartment(data['name'])
                        }}
                      >
                        {departments.map((dep) => <option value={JSON.stringify(dep)} key={dep['_id']['$oid']}>{dep['icon']} {dep['name']}</option>)}
                      </Select>
                      <FormErrorMessage>Avdeling er påkrevd</FormErrorMessage>
                    </FormControl>
                    <Heading size='sm'>Email</Heading>
                    <FormControl isRequired isInvalid={userEmailInvalid}>
                      <Input value={userEmail} onChange={(event) => setUserEmail(event.target.value)} placeholder='Email' />
                      <FormErrorMessage>Email er påkrevd</FormErrorMessage>
                    </FormControl>
                    
                    <Heading size='sm'>Fornavn</Heading>
                    <FormControl isRequired isInvalid={userFirstNameInvalid}>
                      <Input value={userFirstName} onChange={(event) => setUserFirstName(event.target.value)} placeholder='Fornavn' />
                      <FormErrorMessage>Fornavn er påkrevd</FormErrorMessage>
                    </FormControl>
                    
                    <Heading size='sm'>Etternavn</Heading>
                    <FormControl isRequired isInvalid={userLastNameInvalid}>
                      <Input value={userLastName} onChange={(event) => setUserLastName(event.target.value)} placeholder='Etternavn' />
                      <FormErrorMessage>Etternavn er påkrevd</FormErrorMessage>
                    </FormControl>
                      
                    <Heading size='sm'>Passord</Heading>
                    <FormControl isRequired isInvalid={userPasswordInvalid}>
                      <InputGroup>
                        <Input value={userPassword} onChange={(event) => setUserPassword(event.target.value)} placeholder='Passord' type={userHidePassword ? 'text' : 'password'}/>
                        <InputRightElement width='4.5rem'>
                          <Button colorScheme='brand' h='1.75rem' size='sm' onClick={() => {setUserHidePassword(!userHidePassword)}}>{userHidePassword ? 'Skjul' : 'Vis'}</Button>
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>Passord er påkrevd</FormErrorMessage>
                    </FormControl>
                  </Stack>
                </ModalBody>
                <ModalFooter>
                  <Button onClick={onUserClose} colorScheme='red'>Avbryt</Button>
                  <Spacer/>
                  <Button onClick={createUser} colorScheme='brand'>Opprett</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </CardFooter>}
        </Card>        
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap='20px' mb='20px'>
        <ChipTable columnsData={columnsDataCheck} tableData={chips} setTableData={setChips} departmentsData={departments} setAlert={setAlert} />
      </SimpleGrid>
    </Box>
  );
}
